<template>
    <BaseModal :title="'Create Contact'" @close="closeModal">
        <template #modalContent>
            <div class="modal-content-wrapper">
                <div>
                    <h3 class="modal-heading text-xl font-bold text-white p-2">Add new Contact</h3>
                </div>
                <div class="flex items-start justify-start h-full w-full">
                    <form @submit.prevent="addContact" class="flex flex-col gap-4 mt-4 w-1/2">
                        <div>
                            <label for="name" class="text-lg text-gray-400">Name</label>
                            <input id="name" v-model="newContact.name" type="text" placeholder="// John Doe"
                                class="input-field text-lg p-2 w-full" required @input="validateName" />
                            <p v-if="nameError" class="text-red-500 text-[10px] mt-1">{{ nameError }}</p>
                        </div>
                        <div>
                            <label for="email" class="text-lg text-gray-400">Email</label>
                            <input id="email" v-model="newContact.email" type="email" placeholder="// example@gmail.com"
                                class="input-field text-lg p-2 w-full" required @input="validateEmail" />
                            <p v-if="emailError" class="text-red-500 text-[10px] mt-1">{{ emailError }}</p>
                        </div>
                        <div>
                            <label for="tags" class="text-lg text-gray-400">Tags</label>
                            <div class="dropdown-container relative">
                                <div class="input-field flex items-center flex-wrap gap-1 p-2 min-h-[40px]"
                                    @click.stop="toggleDropdown" @keydown.enter="toggleDropdown" tabindex="0"
                                    aria-label="Select tags" role="combobox" :aria-expanded="showDropdown">
                                    <span v-if="newContact.tags.length === 0" class="text-gray-400">
                                        // Select tags...
                                    </span>
                                    <div class="selected-tags flex flex-wrap gap-1">
                                        <span v-for="tag in newContact.tags" :key="tag" class="tag-chip">
                                            {{ tag }}
                                            <button class="remove-tag text-xl" @click.stop="removeTag(tag)"
                                                :aria-label="`Remove tag ${tag}`">
                                                ×
                                            </button>
                                        </span>
                                    </div>
                                    <span class="dropdown-arrow ml-auto">▼</span>
                                </div>
                                <transition name="dropdown">
                                    <div v-if="showDropdown" class="dropdown-menu">
                                        <div v-for="tag in tags" :key="tag" class="dropdown-item"
                                            :class="{ selected: newContact.tags.includes(tag) }"
                                            @click.stop="toggleTag(tag)" @keydown.enter="toggleTag(tag)" tabindex="0"
                                            role="option" :aria-selected="newContact.tags.includes(tag)">
                                            <span>{{ tag }}</span>
                                            <span v-if="newContact.tags.includes(tag)" class="checkmark">✓</span>
                                        </div>
                                    </div>
                                </transition>
                            </div>
                        </div>
                        <div class="flex gap-2 w-full items-center justify-start mt-4">
                            <button type="submit" class="btn-create text-lg py-2 w-1/2 cursor-pointer"
                                :disabled="!!emailError || !!nameError">
                                Add Contact
                            </button>
                            <button type="button" @click="closeModal" class="btn-cancel py-2 text-lg w-1/2 cursor-pointer">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import BaseModal from "@/components/common/BaseModal.vue";

export default {
    name: "CreateContactModal",
    components: {
        BaseModal,
    },
    props: {
        tags: {
            type: Array,
            required: true,
        },
        closeModal: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            newContact: {
                name: '',
                email: '',
                tags: [],
            },
            emailError: '',
            nameError: '',
            showDropdown: false,
        };
    },
    methods: {
        validateEmail() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!this.newContact.email) {
                this.emailError = 'Email required';
            } else if (!emailRegex.test(this.newContact.email)) {
                this.emailError = 'Invalid email';
            } else {
                this.emailError = '';
            }
        },
        validateName() {
            if (!this.newContact.name) {
                this.nameError = 'Name required';
            } else {
                this.nameError = '';
            }
        },
        addContact() {
            this.validateName();
            this.validateEmail();
            if (this.newContact.email && !this.emailError && this.newContact.name && !this.nameError) {
                this.$emit('add-contact', { ...this.newContact });
                this.resetForm();
                this.closeModal();
            } else {
                this.$vs.notify({
                    title: 'Error!',
                    text: 'Please provide a valid name and email',
                    color: 'danger',
                });
            }
        },
        resetForm() {
            this.newContact = { name: '', email: '', tags: [] };
            this.emailError = '';
            this.nameError = '';
            this.showDropdown = false;
        },
        toggleDropdown(event) {
            this.showDropdown = !this.showDropdown;
        },
        toggleTag(tag) {
            if (this.newContact.tags.includes(tag)) {
                this.newContact.tags = this.newContact.tags.filter((t) => t !== tag);
            } else {
                this.newContact.tags.push(tag);
            }
        },
        removeTag(tag) {
            this.newContact.tags = this.newContact.tags.filter((t) => t !== tag);
        },
        handleOutsideClick(event) {
            const dropdown = this.$el.querySelector('.dropdown-container');
            if (dropdown && !dropdown.contains(event.target)) {
                this.showDropdown = false;
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
    },
};
</script>

<style scoped>
.btn-create,
.btn-cancel {
    padding: 4px 8px;
    /* border-radius: 4px; */
    font-size: 12px;
    font-weight: 600;
    transition: all 0.3s;
    line-height: 1.2;
}

.btn-create {
    background: #22c55e;
    color: #000;
}

.btn-create:hover {
    background: #16a34a;
}

.btn-create:disabled {
    background: #4b5563;
    cursor: not-allowed;
}

.btn-cancel {
    background: #1a1a1a;
    border: 1px solid #ff5555;
    color: #ff5555;
}

.btn-cancel:hover {
    background: #ff5555;
    color: #000;
}

.tag-chip {
    background: #22c55e;
    color: #000;
    padding: 2px 6px;
    /* border-radius: 12px; */
    font-size: 10px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.remove-tag {
    background: none;
    border: none;
    color: #000;
    /* font-size: 12px; */
    line-height: 1;
    cursor: pointer;
}

.remove-tag:hover {
    color: #ff5555;
}

.dropdown-container {
    border: 1px solid #22c55e;
}

.dropdown-arrow {
    color: #9ca3af;
    font-size: 10px;
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #1a1a1a;
    border: 1px solid #374151;
    /* border-radius: 4px; */
    max-height: 150px;
    overflow-y: auto;
    z-index: 100;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.dropdown-item {
    padding: 6px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #fff;
    cursor: pointer;
}

.dropdown-item:hover,
.dropdown-item:focus {
    background: #22c55e;
    color: #000;
}

.dropdown-item.selected {
    background: #22c55e;
    color: #000;
}

.checkmark {
    color: #000;
    font-size: 12px;
}
</style>